import React, { useEffect } from "react";
import * as microsoftTeams from "@microsoft/teams-js";
import { IAppConfig } from "ict24h-themes-react/lib/esm/App";
import { Ict24hThemes, useThemesConfig } from "ict24h-themes-react";
import dayjs, { Dayjs } from "dayjs";
import quarterOfYear from "dayjs/plugin/quarterOfYear";
import AppRouters from "./AppRouter";
import { message } from "antd";
import jwt_decode from "jwt-decode";

import advancedFormat from "dayjs/plugin/advancedFormat";
import customParseFormat from "dayjs/plugin/customParseFormat";
import localeData from "dayjs/plugin/localeData";
import weekday from "dayjs/plugin/weekday";
import weekOfYear from "dayjs/plugin/weekOfYear";
import weekYear from "dayjs/plugin/weekYear";
import isLeapYear from "dayjs/plugin/isLeapYear"; // import plugin

import "dayjs/locale/vi";
import isInICTMobileApp from "./utils/is-ict-mobile-app";
import { ICT_INTERNAL_TENANTS } from "./constants/ict-internal-tenants";
import getGraphAccessToken from "./utils/get-graph-token";
import {
  NAVI_INTER_KEY,
  getDestinationModuleRoute,
} from "./modules/navi-inter/NaviInter";

dayjs.locale("vi");

dayjs.extend(customParseFormat);
dayjs.extend(advancedFormat);
dayjs.extend(weekday);
dayjs.extend(localeData);
dayjs.extend(weekOfYear);
dayjs.extend(weekYear);
dayjs.extend(quarterOfYear);
dayjs.extend(isLeapYear); // use plugin

const isInMobileApp = isInICTMobileApp();

const appConfig: IAppConfig = {
  authAPIEndpoint: " https://approval.zbh-dev.i24h.app",
  localStorageKey: "i24h-dev-approval-auth",
  userAPIEndpoint: " https://approval.zbh-dev.i24h.app/user",
  loginProvider: "ms-obh", // NOTE: đang lỗi do app mobile của MS cũng dùng react native webview // isInMobileApp ? undefined : "ms-obh",
  routerType: "browser",
};

function App() {
  const [forgeReady, setForgeReady] = React.useState<string>();
  const [appConfigState, setAppConfigState] =
    React.useState<IAppConfig>(appConfig);

  const themeConfig = useThemesConfig();

  // call when rendering the component
  // initLoginToken(ssoLoginSuccess,ssoLoginFailure, setCurrentUser, setLoading).catch(error => console.error(error));
  useEffect(() => {
    // Initialize the Microsoft Teams SDK
    microsoftTeams.app
      .initialize()
      .then(() => {
        // 1. Chờ Host tải xong ngữ cảnh định danh (context) để đảm bảo SSO Broker đã sẵn sàng
        return microsoftTeams.app.getContext();
      })
      .then((context) => {
        // Xử lý Teams Deep Link nếu có subEntityId / subPageId trong context
        const teamsSubPath =
          (context?.page as any)?.subEntityId ||
          (context?.page as any)?.subPageId;
        if (
          teamsSubPath &&
          typeof teamsSubPath === "string" &&
          teamsSubPath.trim() !== ""
        ) {
          const cleanSubPath = teamsSubPath.trim();
          console.log(
            "[TeamsDeepLink] Nhận subEntityId từ Teams Context:",
            cleanSubPath,
          );
          localStorage.setItem(NAVI_INTER_KEY, cleanSubPath);

          const destModule = getDestinationModuleRoute(cleanSubPath);
          if (
            window.location.pathname === "/" ||
            window.location.pathname === "/home" ||
            window.location.hash === "" ||
            window.location.hash === "#/home"
          ) {
            window.location.hash = `#${destModule}`;
          }
        }

        // 2. Hàm thử lấy Token có cơ chế Retry (thử lại) để phòng xa trường hợp chậm trễ mạng
        const getTokenWithRetry = async (
          retries = 3,
          delayMs = 1500,
        ): Promise<string> => {
          for (let i = 0; i < retries; i++) {
            try {
              return await microsoftTeams.authentication.getAuthToken();
            } catch (err) {
              console.warn(
                `[SSO] Lần ${i + 1} lấy token thất bại. Thử lại sau ${delayMs}ms...`,
                err,
              );
              if (i === retries - 1) throw err;
              await new Promise((resolve) => setTimeout(resolve, delayMs));
            }
          }
          throw new Error("Không thể lấy token sau nhiều lần thử.");
        };

        return getTokenWithRetry();
      })
      .then((result: string) => {
        themeConfig.initLoginToken = result;

        hasMicrosoft365License()
          .then((hasLicense) => {
            console.log("hasLicense: ", hasLicense)
          })
          .catch((err) => {
            console.error("Lỗi kiểm tra giấy phép Microsoft 365:", err);
          })

        // xử lý lấy tenantID nào
        let tidFromToken = undefined; // Fallback
        try {
          const decodedToken: any = jwt_decode(result);
          if (decodedToken && decodedToken.tid) {
            tidFromToken = decodedToken.tid;
          }
        } catch (e) {
          console.error("Lỗi giải mã token bằng jwt-decode:", e);
        }

        if (tidFromToken === ICT_INTERNAL_TENANTS.cenprecast.tid) {
          setAppConfigState((prev) => {
            return {
              ...prev,
              "i24h-tenant": ICT_INTERNAL_TENANTS.cenprecast.i24hTenant,
            };
          });
        }

        // set cái này để render lại
        setForgeReady(result);
      })
      .catch((err) => {
        console.log("SSO error: >>>>>> ", JSON.stringify(err, null, 4));
        console.error("Lỗi xác minh SSO:", err);

        if (isInMobileApp) return;

        message.error(
          "Không thể kết nối MS 365 hoặc xác minh tài khoản của bạn",
        );

        // navigate to Login tab
        microsoftTeams.pages.currentApp.navigateTo({
          pageId: "ict24h.m365.tabs.login-m365",
        });
      });
  }, []);
  // Xác định xem có nên render ứng dụng hay không
  const isRender = isInMobileApp
    ? true // Nếu đang chạy trong ứng dụng di động, luôn render
    : !!(themeConfig.initLoginToken && forgeReady); // Nếu không phải ứng dụng di động, chỉ render khi có token và state đã sẵn sàng



  return (
    <>
      {isRender ? (
        <Ict24hThemes config={appConfigState} appRouters={AppRouters} />
      ) : (
        <>
          <h3>Loading...</h3>
          {/*<Button onClick={showConsentDialog} >open win</Button>*/}
        </>
      )}
    </>
  );
}

export default App;


async function hasMicrosoft365License(
): Promise<boolean> {

  const msToken = await getGraphAccessToken();
  const response = await fetch(
    'https://graph.microsoft.com/v1.0/me/licenseDetails?$select=servicePlans',
    {
      headers: {
        Authorization: `Bearer ${msToken}`,
      },
    },
  )

  if (!response.ok) {
    console.log(
      `Microsoft Graph Error ${response.status}: ${await response.text()}`,
    )
    return false
  }

  const data: {
    value: Array<{
      servicePlans: Array<{
        servicePlanName: string
        provisioningStatus: string
      }>
    }>
  } = await response.json()

  console.log(' >>> licenseDetails data: ',data)

  const requiredPlans = new Set([
    // Teams
    'TEAMS1',

    // Exchange
    'EXCHANGE_S_STANDARD',
    'EXCHANGE_S_ENTERPRISE',

    // SharePoint
    'SHAREPOINTSTANDARD',
    'SHAREPOINTENTERPRISE',
  ])

  return data.value.some((license) =>
    license.servicePlans.some(
      (plan) =>
        plan.provisioningStatus === 'Success' &&
        requiredPlans.has(plan.servicePlanName),
    ),
  )
}
